import React, { useState, useContext, useMemo } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import clsx from 'clsx';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

function PurchaseTracking() {
  const { uinfo } = useContext(GlobalContext);
  const [activeView, setActiveView] = useState('today'); // 'today' or 'yesterday'

  const { data: customerSalesData, isLoading } = useApi({
    url: `/visiondashboard/rankCustomers?Rep=${uinfo?.Inits}`,
    initial: []
  });

  const { data: salesMarginByBranchAndAccountToday } = useApi({
    url: '/salesassist/cplusassist/salesMarginByBranchAndAccountToday',
    initial: []
  });

  // Process customer data
  const processedData = useMemo(() => {
    if (!customerSalesData || customerSalesData.length === 0) {
      return {
        purchasedToday: [],
        notPurchasedToday: [],
        purchasedYesterday: [],
        notPurchasedYesterday: []
      };
    }

    const purchasedToday = [];
    const notPurchasedToday = [];
    const purchasedYesterday = [];
    const notPurchasedYesterday = [];

    customerSalesData.forEach((customer) => {
      const customerData = salesMarginByBranchAndAccountToday.find(el => el.account === customer._id);
      const hasQuotes = customerData?.quotesCount > 0;

      // Today's purchases
      if (customer.totalSalesToday > 0) {
        purchasedToday.push({
          ...customer,
          purchaseAmount: customer.totalSalesToday,
          hasQuotes
        });
      } else {
        notPurchasedToday.push({
          ...customer,
          hasQuotes
        });
      }

      // Yesterday's purchases
      if (customer.totalSalesYesterday > 0) {
        purchasedYesterday.push({
          ...customer,
          purchaseAmount: customer.totalSalesYesterday
        });
      } else {
        notPurchasedYesterday.push({
          ...customer
        });
      }
    });

    // Sort by purchase amount (descending)
    purchasedToday.sort((a, b) => b.purchaseAmount - a.purchaseAmount);
    purchasedYesterday.sort((a, b) => b.purchaseAmount - a.purchaseAmount);

    return {
      purchasedToday,
      notPurchasedToday,
      purchasedYesterday,
      notPurchasedYesterday
    };
  }, [customerSalesData, salesMarginByBranchAndAccountToday]);

  const renderTable = (customers, showAmount = false) => {
    if (customers.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No customers found.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Garage Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              {showAmount && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Amount
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                30d Sales
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Purchase
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {customers.map((customer, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  {showAmount ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                  {customer._id}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {customer.GarageName}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={clsx(
                    'px-2 py-1 rounded-full text-white text-xs',
                    customer.CustomerRank === 'A' && 'bg-green-500',
                    customer.CustomerRank === 'B' && 'bg-blue-500',
                    customer.CustomerRank === 'C' && 'bg-orange-500',
                    customer.CustomerRank === 'D' && 'bg-yellow-500',
                    customer.CustomerRank === 'E' && 'bg-purple-500',
                    customer.CustomerRank === 'F' && 'bg-red-500',
                    customer.CustomerRank === 'N/A' && 'bg-gray-500'
                  )}>
                    {customer.CustomerRank || 'N/A'}
                  </span>
                </td>
                {showAmount && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                    £{formatNumber(customer.purchaseAmount)}
                  </td>
                )}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  £{formatNumber(customer.totalSalesLast30Days)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {customer.lastOrderDate 
                    ? new Date(customer.lastOrderDate).toLocaleDateString('en-GB')
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Loading purchase data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('today')}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              activeView === 'today'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Today's Purchases
          </button>
          <button
            onClick={() => setActiveView('yesterday')}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              activeView === 'yesterday'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Yesterday's Purchases
          </button>
        </div>
      </div>

      {/* Today's View */}
      {activeView === 'today' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purchased Today */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span>Purchased Today ({processedData.purchasedToday.length})</span>
              </h3>
            </div>
            {renderTable(processedData.purchasedToday, true)}
          </div>

          {/* Not Purchased Today */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <XCircleIcon className="w-5 h-5 text-red-500" />
                <span>Didn't Purchase Today ({processedData.notPurchasedToday.length})</span>
              </h3>
            </div>
            {renderTable(processedData.notPurchasedToday, false)}
          </div>
        </div>
      )}

      {/* Yesterday's View */}
      {activeView === 'yesterday' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purchased Yesterday */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span>Purchased Yesterday ({processedData.purchasedYesterday.length})</span>
              </h3>
            </div>
            {renderTable(processedData.purchasedYesterday, true)}
          </div>

          {/* Not Purchased Yesterday */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <XCircleIcon className="w-5 h-5 text-red-500" />
                <span>Didn't Purchase Yesterday ({processedData.notPurchasedYesterday.length})</span>
              </h3>
            </div>
            {renderTable(processedData.notPurchasedYesterday, false)}
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaseTracking;

